import React, { useState, useEffect, useRef } from 'react';
import type { GameSettings, Exercise } from '../types';
import { problemGenerator } from '../algorithms/problemGenerator';

interface MathProblemProps {
  settings: GameSettings;
  onCorrectAnswer: (exercise: Omit<Exercise, 'id' | 'timestamp' | 'isCorrect'>) => void;
  onWrongAnswer: (exercise: Omit<Exercise, 'id' | 'timestamp' | 'isCorrect'>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

// Preload audio files
const correctSound = new Audio('/sounds/correct.mp3');
const wrongSound = new Audio('/sounds/wrong.mp3');

// Configure audio files
correctSound.preload = 'auto';
wrongSound.preload = 'auto';

export function MathProblem({ settings, onCorrectAnswer, onWrongAnswer, inputRef }: MathProblemProps) {
  const [answer, setAnswer] = useState('');
  const [currentProblem, setCurrentProblem] = useState(() => 
    problemGenerator.generateProblem(
      settings.difficulty,
      settings.selectedOperations,
      settings.mixedOperations
    )
  );
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    problemGenerator.reset();
    const newProblem = problemGenerator.generateProblem(
      settings.difficulty,
      settings.selectedOperations,
      settings.mixedOperations
    );
    setCurrentProblem(newProblem);
  }, [settings]);

  // Handle mobile keyboard appearance
  useEffect(() => {
    const handleFocus = () => {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    };

    const input = inputRef.current;
    if (input) {
      input.addEventListener('focus', handleFocus);
      return () => input.removeEventListener('focus', handleFocus);
    }
  }, []);

  const getOperationSymbol = () => {
    switch (currentProblem.operation) {
      case 'add': return '+';
      case 'subtract': return '-';
      case 'multiply': return '×';
      case 'divide': return '÷';
      default: return '';
    }
  };

  const playSound = async (isCorrect: boolean) => {
    try {
      const audio = isCorrect ? correctSound : wrongSound;
      audio.currentTime = 0;
      await audio.play();
    } catch (error) {
      console.warn('Could not play sound:', error);
    }
  };

  const normalizeDecimal = (value: string): number => {
    const normalized = value.replace(',', '.');
    return parseFloat(normalized);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }
    
    if (!answer.trim()) {
      setFeedback({
        message: 'Wpisz odpowiedź!',
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    const userAnswer = normalizeDecimal(answer);
    const exercise = {
      operation: currentProblem.operation,
      num1: currentProblem.num1,
      num2: currentProblem.num2,
      userAnswer,
      correctAnswer: currentProblem.expectedAnswer
    };

    const isCorrect = Math.abs(userAnswer - currentProblem.expectedAnswer) < 0.01;

    if (isCorrect) {
      setFeedback({
        message: 'Brawo! Poprawna odpowiedź!',
        type: 'success'
      });
      onCorrectAnswer(exercise);
      await playSound(true);
    } else {
      const formattedAnswer = currentProblem.operation === 'divide' 
        ? currentProblem.expectedAnswer.toFixed(2) 
        : currentProblem.expectedAnswer.toString();
      
      setFeedback({
        message: `Spróbuj jeszcze raz! Poprawna odpowiedź to: ${formattedAnswer}`,
        type: 'error'
      });
      onWrongAnswer(exercise);
      await playSound(false);
    }

    setTimeout(() => {
      setFeedback(null);
      setAnswer('');
      setIsSubmitting(false);
      
      const newProblem = problemGenerator.generateProblem(
        settings.difficulty,
        settings.selectedOperations,
        settings.mixedOperations
      );
      setCurrentProblem(newProblem);
    }, 2000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow digits, one decimal separator (either . or ,), and minus sign
    if (/^-?\d*[.,]?\d*$/.test(value)) {
      setAnswer(value);
    }
  };

  return (
    <div ref={containerRef} className="bg-white p-4 rounded-lg shadow-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-center">
          <div className="flex items-center justify-center flex-wrap gap-2">
            <span className="text-2xl sm:text-3xl font-bold">
              {currentProblem.num1} {getOperationSymbol()} {currentProblem.num2} =
            </span>
            <input
              ref={inputRef}
              type="text"
              value={answer}
              onChange={handleInputChange}
              className="w-20 sm:w-24 text-2xl sm:text-3xl font-bold text-center border-b-2 border-blue-500 focus:outline-none focus:border-blue-700"
              autoFocus
              inputMode="decimal"
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        {feedback && (
          <div
            className={`text-center p-2 rounded-md ${
              feedback.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {feedback.message}
          </div>
        )}

        <button
          type="submit"
          className={`w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors text-lg ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={isSubmitting}
        >
          Sprawdź
        </button>
      </form>
    </div>
  );
}